import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useApp } from "../AppContext";
import { NAV, TRUST, UI } from "../content";
import { OmMark } from "./Motion";

function FontToggle() {
  const { fontSize, setFontSize } = useApp();
  const opts = [
    { k: "small", label: "A-" },
    { k: "normal", label: "A" },
    { k: "large", label: "A+" },
  ];
  return (
    <div className="flex items-center rounded-full border border-earth-900/15 overflow-hidden bg-sand-50/95 shadow-sm" data-testid="font-toggle">
      {opts.map((o) => (
        <button
          key={o.k}
          data-testid={`font-${o.k}-btn`}
          onClick={() => setFontSize(o.k)}
          className={`px-2.5 py-1 text-xs font-semibold transition-colors ${fontSize === o.k ? "bg-saffron-500 text-white" : "text-earth-800 hover:bg-sand-100"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function LangToggle() {
  const { lang, setLang } = useApp();
  return (
    <div className="flex items-center rounded-full border border-earth-900/15 overflow-hidden bg-sand-50/95 shadow-sm" data-testid="lang-toggle">
      {["mr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-deepred-700 text-white" : "text-earth-800 hover:bg-sand-100"}`}
        >
          {l === "mr" ? "मराठी" : "EN"}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const L = (o) => o[lang] ?? o.en;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-sand-50/90 backdrop-blur-xl border-b border-earth-900/10 shadow-[0_8px_30px_rgba(59,34,19,0.06)]" : "bg-transparent"}`}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <span className={`${scrolled ? "text-deepred-700" : "text-white"} transition-colors`}><OmMark className="w-9 h-9" /></span>
            <div className="leading-tight">
              <div className={`font-serif-head text-lg lg:text-xl font-bold ${scrolled ? "text-earth-900" : "text-white text-shadow-lg"}`}>
                {lang === "mr" ? TRUST.short_mr : TRUST.short_en}
              </div>
              <div className={`text-[10px] uppercase tracking-[0.2em] ${scrolled ? "text-saffron-600" : "text-ochre-400 text-shadow-lg"}`}>Audumbar</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV.slice(1, 9).map((n) => (
              <Link
                key={n.path}
                to={n.path}
                data-testid={`nav-${n.path === "/" ? "home" : n.path.slice(1)}`}
                className={`px-2.5 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === n.path ? "text-saffron-600" : scrolled ? "text-earth-800 hover:text-saffron-600" : "text-white/90 hover:text-white text-shadow-lg"}`}
              >
                {L(n)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-2"><LangToggle /><FontToggle /></div>
            <div className="lg:hidden"><LangToggle /></div>
            <Link to="/donate" data-testid="header-donate-btn" className="hidden sm:inline-flex items-center rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5">
              {UI.donate_now[lang]}
            </Link>
            <button className="xl:hidden p-2 text-earth-900" onClick={() => setOpen(true)} data-testid="menu-open-btn">
              <Menu className={scrolled ? "text-earth-900" : "text-white"} />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-earth-900/95 backdrop-blur-md xl:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center h-16 px-4">
            <span className="font-serif-head text-white text-xl">{lang === "mr" ? TRUST.short_mr : TRUST.short_en}</span>
            <button onClick={() => setOpen(false)} className="p-2 text-white" data-testid="menu-close-btn"><X /></button>
          </div>
          <div className="px-6 py-4 flex flex-col gap-1 overflow-y-auto max-h-[80vh]">
            {NAV.map((n) => (
              <Link key={n.path} to={n.path} data-testid={`mnav-${n.path === "/" ? "home" : n.path.slice(1)}`} className="py-3 text-lg text-sand-100 border-b border-white/10">{L(n)}</Link>
            ))}
            <div className="flex gap-3 mt-4"><LangToggle /><FontToggle /></div>
            <Link to="/contact" className="mt-4 inline-flex items-center gap-2 text-ochre-400"><Phone size={16} /> {TRUST.phone_after}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
